// desktopNotification — thin wrapper over the Web Notifications API (Tier B).
// -----------------------------------------------------------------
// App-level job notifications, tier B (browser/OS notification). The in-app
// Mantine toast (tier A) covers "I moved to another screen IN the SPA". This
// module adds the OS-level notification for "I tabbed away / switched apps
// while the SPA tab is still open" — i.e. when the document is BACKGROUNDED.
//
// Scope + limits (deliberate):
//   - This needs the SPA tab to remain OPEN. True "app fully closed" push
//     requires a Service Worker + Push subscription + a backend that pushes on
//     job completion (tier C) — out of scope, tracked separately.
//   - We only fire when `document.visibilityState === 'hidden'`. When the tab
//     is visible the Mantine toast already conveys the outcome, so a redundant
//     OS notification would just be noise.
//   - Permission is OPT-IN via `requestDesktopNotificationPermission()`, which
//     MUST be called from a real user gesture (browsers reject a bare
//     `Notification.requestPermission()` otherwise). See <EnableNotifications>.
//
// SSR / jsdom safe: every entry point guards on `typeof Notification`, so tests
// (jsdom has no Notification constructor) and any non-browser context no-op.

/** True iff the Web Notifications API exists in this environment. */
export const desktopNotificationsSupported = (): boolean =>
  typeof window !== 'undefined' && typeof Notification !== 'undefined';

/** Notification permission states (mirrors the DOM `NotificationPermission`
 *  union; declared locally so eslint's `no-undef` doesn't trip on the ambient
 *  DOM type name). */
export type DesktopPermission = 'default' | 'granted' | 'denied';

/** Current permission ('default' | 'granted' | 'denied'), or 'unsupported'. */
export const desktopNotificationPermission = ():
  | DesktopPermission
  | 'unsupported' =>
  desktopNotificationsSupported()
    ? (Notification.permission as DesktopPermission)
    : 'unsupported';

/**
 * Ask the browser for notification permission. Resolves to the resulting
 * permission (or 'unsupported'). MUST be invoked from a user gesture (click).
 */
export const requestDesktopNotificationPermission = async (): Promise<
  DesktopPermission | 'unsupported'
> => {
  if (!desktopNotificationsSupported()) return 'unsupported';
  try {
    return (await Notification.requestPermission()) as DesktopPermission;
  } catch {
    // Older Safari used a callback signature that can throw on the promise
    // form; treat any failure as "no permission granted".
    return Notification.permission as DesktopPermission;
  }
};

/**
 * Fire an OS notification IF (a) supported, (b) permission granted, and (c) the
 * admin document is NOT currently focused. A no-op otherwise — callers can
 * invoke this unconditionally alongside their in-app toast.
 *
 * We gate on `document.hasFocus()` rather than `visibilityState`: a tab is only
 * `visibilityState === 'hidden'` when it isn't the selected tab IN ITS OWN
 * WINDOW, so browsing another site in a SEPARATE WINDOW (admin tab still the
 * active tab in its window, just behind) would leave it `'visible'` and wrongly
 * suppress the notification. `hasFocus()` is false whenever the operator isn't
 * actually looking at the admin document — other tab, other window, or other
 * app — which is exactly when the OS notification adds value over the toast.
 *
 * `tag` collapses duplicate notifications for the same logical event (e.g. a
 * job id) so a re-fire replaces rather than stacks.
 */
export const maybeNotifyDesktop = (input: {
  title: string;
  body: string;
  tag?: string;
}): void => {
  if (!desktopNotificationsSupported()) return;
  if (Notification.permission !== 'granted') return;
  // Only when the operator is NOT looking at this document — otherwise the
  // in-app toast already covers it. `hasFocus()` false ⇒ another tab/window/app
  // is focused.
  if (
    typeof document !== 'undefined' &&
    typeof document.hasFocus === 'function' &&
    document.hasFocus()
  ) {
    return;
  }
  try {
    const notification = new Notification(input.title, {
      body: input.body,
      // Persist on screen until the operator dismisses it, rather than
      // auto-hiding after a few seconds (the default). The whole point is
      // "start a long job, go do something else, find the result waiting" — an
      // auto-dismissing banner defeats that if they glance back a moment late.
      // NOTE: on macOS true persistence also needs the browser's notification
      // style set to "Alerts" (System Settings → Notifications → <browser>);
      // "Banners" auto-hide regardless. Either way it lands in Notification
      // Center.
      requireInteraction: true,
      ...(input.tag !== undefined ? { tag: input.tag } : {}),
    });
    void notification;
  } catch {
    // Some environments throw if the API is present but disabled; never let a
    // notification failure bubble into the job-watching effect.
  }
};

/**
 * Fire a one-off test OS notification REGARDLESS of tab visibility (so the
 * operator can confirm notifications work without backgrounding the tab).
 * No-op unless supported + permission granted.
 */
export const sendTestDesktopNotification = (): void => {
  if (!desktopNotificationsSupported()) return;
  if (Notification.permission !== 'granted') return;
  try {
    const notification = new Notification('Odyssée Admin', {
      body: 'Desktop notifications are on — you’ll be pinged when a background job finishes.',
      requireInteraction: true,
    });
    void notification;
  } catch {
    // Never let a test-notification failure surface as an error.
  }
};
